"use client";

import { useEffect, useState, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { api } from "@/lib/api";
import type { UsageCurrent, UsageHistoryRow } from "@/lib/types/usage";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { DocsLink } from "@/components/dashboard/docs-link";

// ── helpers ────────────────────────────────────────────────────────────────

/** -1 means unlimited */
function isUnlimited(limit: number) {
  return limit < 0;
}

function pct(used: number, limit: number): number {
  if (isUnlimited(limit) || limit === 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

function barColor(percent: number, unlimited: boolean): string {
  if (unlimited) return "bg-muted-foreground/30";
  if (percent >= 80) return "bg-destructive";
  if (percent >= 50) return "bg-warning";
  return "bg-profit";
}

/** "2026-04" → "Apr 2026" */
function formatPeriodFull(period: string): string {
  const [year, month] = period.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

/** "2026-04" → "Apr" */
function formatPeriodShort(period: string): string {
  const [year, month] = period.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString("en-US", { month: "short" });
}

// ── sub-components ─────────────────────────────────────────────────────────

function CounterCard({
  label,
  used,
  limit,
  unlimited: unlimitedLabel,
  limitReachedLabel,
}: {
  label: string;
  used: number;
  limit: number;
  unlimited: string;
  limitReachedLabel: string;
}) {
  const unlimited = isUnlimited(limit);
  const percent = pct(used, limit);
  const color = barColor(percent, unlimited);
  const limitReached = !unlimited && used >= limit;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5">
      <p className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </p>

      <div className="flex items-baseline gap-2">
        <span className="font-heading text-4xl font-extrabold leading-none tracking-tight tabular-nums">
          {used.toLocaleString()}
        </span>
        <span className="text-sm text-muted-foreground">
          / {unlimited ? unlimitedLabel : limit.toLocaleString()}
        </span>
        {limitReached && (
          <span className="ms-auto rounded-xl border border-destructive/60 bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-destructive">
            {limitReachedLabel}
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-1.5 w-full overflow-hidden rounded-xl bg-muted">
        <div
          className={cn("h-full transition-all duration-500", color)}
          style={{ width: unlimited ? "0%" : `${percent}%` }}
        />
      </div>

      {!unlimited && (
        <p className="text-xs text-muted-foreground">
          {percent}% used
        </p>
      )}
    </div>
  );
}

function CounterCardSkeleton() {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5">
      <Skeleton className="h-3 w-28" />
      <Skeleton className="h-10 w-36" />
      <Skeleton className="h-1.5 w-full" />
      <Skeleton className="h-3 w-16" />
    </div>
  );
}

function UpgradeCta({
  percent,
  dimension,
  period,
  title,
  body: bodyTemplate,
  button,
}: {
  percent: number;
  dimension: string;
  period: string;
  title: string;
  body: string;
  button: string;
}) {
  const body = bodyTemplate
    .replace("{percentage}", String(percent))
    .replace("{dimension}", dimension)
    .replace("{period}", formatPeriodFull(period));

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-warning/40 bg-warning/5 p-5 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
        <div>
          <p className="text-sm font-semibold">{title}</p>
          <p className="mt-0.5 text-sm text-muted-foreground">{body}</p>
        </div>
      </div>
      <Button size="sm" asChild className="shrink-0">
        <Link href="/settings/billing">{button}</Link>
      </Button>
    </div>
  );
}

// ── main component ─────────────────────────────────────────────────────────

export interface UsageViewProps {
  /** If provided, fetches via admin-scoped endpoints. Otherwise uses self-scoped. */
  userId?: string;
  /** Months of history to fetch and render. Default 12. */
  months?: number;
  /** Whether to render the "Upgrade" CTA when any dimension >= 80%. Default true. */
  showUpgradeCta?: boolean;
  /** Whether to render the docs link below the header. Default true (user-facing). */
  showDocsLink?: boolean;
  /** Whether to render the page header. Default true (user-facing). Set false when embedded. */
  showHeader?: boolean;
}

export function UsageView({
  userId,
  months = 12,
  showUpgradeCta = true,
  showDocsLink = true,
  showHeader = true,
}: UsageViewProps) {
  const t = useTranslations();
  const [current, setCurrent] = useState<UsageCurrent | null>(null);
  const [history, setHistory] = useState<UsageHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cur, hist] = await Promise.all([
        userId ? api.admin.users.usage.current(userId) : api.usage.current(),
        userId
          ? api.admin.users.usage.history(userId, months)
          : api.usage.history(months),
      ]);
      setCurrent(cur);
      setHistory(hist);
    } catch {
      setError("Failed to load usage data.");
    } finally {
      setLoading(false);
    }
  }, [userId, months]);

  useEffect(() => {
    load();
  }, [load]);

  // Compute upgrade CTA
  let upgradeDimension: string | null = null;
  let upgradePercent = 0;

  if (current) {
    const candidates: Array<{ label: string; used: number; limit: number }> = [
      { label: t("dashboard.usagePage.cards.signalsIn"), used: current.signalsIn, limit: current.signalsInLimit },
      { label: t("dashboard.usagePage.cards.deliveries"), used: current.deliveries, limit: current.deliveriesLimit },
      { label: t("dashboard.usagePage.cards.aiCredits"), used: current.aiCreditsUsed, limit: current.aiCreditsLimit },
    ];
    for (const c of candidates) {
      if (!isUnlimited(c.limit)) {
        const p = pct(c.used, c.limit);
        if (p >= 80 && p > upgradePercent) {
          upgradePercent = p;
          upgradeDimension = c.label;
        }
      }
    }
  }

  // Chart data, add short period label
  const chartData = history.map((row) => ({
    ...row,
    label: formatPeriodShort(row.period),
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      {showHeader && (
        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{t("dashboard.usagePage.subtitle")}</p>
            {showDocsLink && (
              <div className="mt-2">
                <DocsLink href="/docs/reference/plan-limits">Plan limits explained</DocsLink>
              </div>
            )}
          </div>
          {current && (
            <span className="font-mono text-sm text-muted-foreground">
              {formatPeriodFull(current.period)}
            </span>
          )}
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="flex items-center justify-between rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="ghost" size="sm" onClick={load}>
            <RefreshCw className="me-2 h-3.5 w-3.5" />
            Retry
          </Button>
        </div>
      )}

      {/* Upgrade CTA */}
      {showUpgradeCta && upgradeDimension && current && (
        <UpgradeCta
          percent={upgradePercent}
          dimension={upgradeDimension}
          period={current.period}
          title={t("dashboard.usagePage.upgradeCta.title")}
          body={t("dashboard.usagePage.upgradeCta.body")}
          button={t("dashboard.usagePage.upgradeCta.button")}
        />
      )}

      {/* Counter cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        {loading || !current ? (
          <>
            <CounterCardSkeleton />
            <CounterCardSkeleton />
            <CounterCardSkeleton />
          </>
        ) : (
          <>
            <CounterCard
              label={t("dashboard.usagePage.cards.signalsIn")}
              used={current.signalsIn}
              limit={current.signalsInLimit}
              unlimited={t("dashboard.usagePage.unlimited")}
              limitReachedLabel={t("dashboard.usagePage.limitReached")}
            />
            <CounterCard
              label={t("dashboard.usagePage.cards.deliveries")}
              used={current.deliveries}
              limit={current.deliveriesLimit}
              unlimited={t("dashboard.usagePage.unlimited")}
              limitReachedLabel={t("dashboard.usagePage.limitReached")}
            />
            <CounterCard
              label={t("dashboard.usagePage.cards.aiCredits")}
              used={current.aiCreditsUsed}
              limit={current.aiCreditsLimit}
              unlimited={t("dashboard.usagePage.unlimited")}
              limitReachedLabel={t("dashboard.usagePage.limitReached")}
            />
          </>
        )}
      </div>

      {/* History chart */}
      <div className="rounded-xl border border-border bg-card">
        <div className="border-b border-border px-5 py-4">
          <p className="text-sm font-semibold">{t("dashboard.usagePage.chart.title")}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{t("dashboard.usagePage.chart.subtitle")}</p>
        </div>

        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Skeleton className="h-48 w-full mx-5" />
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex h-64 items-center justify-center">
            <p className="text-sm text-muted-foreground">{t("dashboard.usagePage.chart.empty")}</p>
          </div>
        ) : (
          // LTR island: time-series charts read oldest to newest in the
          // inline direction, which should not flip in RTL contexts, per
          // spec Section 5 (LTR islands).
          <div className="p-5" dir="ltr">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 0,
                    fontSize: 12,
                  }}
                  labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 12, paddingTop: 12 }}
                  formatter={(value) => {
                    if (value === "signalsIn") return t("dashboard.usagePage.chart.legendSignalsIn");
                    if (value === "deliveries") return t("dashboard.usagePage.chart.legendDeliveries");
                    return value;
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="signalsIn"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  name="signalsIn"
                />
                <Line
                  type="monotone"
                  dataKey="deliveries"
                  stroke="hsl(var(--blue, 220 90% 56%))"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  name="deliveries"
                />
              </LineChart>
            </ResponsiveContainer>

            {/* Accessible table */}
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border">
                    <th className="py-2 pe-4 text-start font-mono uppercase tracking-wide text-muted-foreground">
                      {t("dashboard.usagePage.chart.tablePeriod")}
                    </th>
                    <th className="py-2 pe-4 text-end font-mono uppercase tracking-wide text-muted-foreground">
                      {t("dashboard.usagePage.chart.legendSignalsIn")}
                    </th>
                    <th className="py-2 pe-4 text-end font-mono uppercase tracking-wide text-muted-foreground">
                      {t("dashboard.usagePage.chart.legendDeliveries")}
                    </th>
                    <th className="py-2 text-end font-mono uppercase tracking-wide text-muted-foreground">
                      {t("dashboard.usagePage.chart.tableAiCredits")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[...chartData].reverse().map((row) => (
                    <tr key={row.period} className="border-b border-border/50">
                      <td className="py-1.5 pe-4 font-mono text-muted-foreground">
                        {formatPeriodFull(row.period)}
                      </td>
                      <td className="py-1.5 pe-4 text-end tabular-nums">
                        {row.signalsIn.toLocaleString()}
                      </td>
                      <td className="py-1.5 pe-4 text-end tabular-nums">
                        {row.deliveries.toLocaleString()}
                      </td>
                      <td className="py-1.5 text-end tabular-nums">
                        {row.aiCreditsUsed.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
